import React from "react";
import { useState, useRef, useEffect } from "react";
import { plans, getResolvedFeatures } from '../plans';
import { config } from '../constants';
import { ensureGoogleAnalytics, trackEvent } from '../utils/analytics';
const API_BASE = config.API_BASE;

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

// Helper to get or generate a persistent user ID
function getUserId() {
  let id = localStorage.getItem('chatbotUserId');
  if (!id) {
    id = 'user-' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chatbotUserId', id);
  }
  return id;
}

// rebuild test

export default function Chatbot(props) {
  // Customization state
  const [chatboxEnabled, setChatboxEnabled] = useState(props.chatboxEnabled ?? true);
  const [logoUrl, setLogoUrl] = useState(props.logoUrl ?? '');
  const [header, setHeader] = useState(props.header ?? '');
  const [font, setFont] = useState(props.font ?? '');
  const [fontSize, setFontSize] = useState(props.fontSize ?? '');
  const [botName, setBotName] = useState(props.botName ?? '');
  const [position, setPosition] = useState(props.position ?? '');
  const [shape, setShape] = useState(props.shape ?? '');
  // --- Chat Bubble Color Customization (Prod) ---
  const [chatBubbleOpenColor, setChatBubbleOpenColor] = useState(props.chatBubbleOpenColor || '#2D2D2D');
  const [chatBubbleClosedColor, setChatBubbleClosedColor] = useState(props.chatBubbleClosedColor || '#2D2D2D');
  const [botBubbleColor, setBotBubbleColor] = useState(props.botBubbleColor || '#333333');
  const [userBubbleColor, setUserBubbleColor] = useState(props.userBubbleColor || '#0B93F6');
  const [headerBgColor, setHeaderBgColor] = useState(props.headerBgColor ?? '');
  const [chatBgColor, setChatBgColor] = useState(props.chatBgColor ?? '');
  const [footerBgColor, setFooterBgColor] = useState(props.footerBgColor ?? '');
  const [chatInputBgColor, setChatInputBgColor] = useState(props.chatInputBgColor ?? '');
  const [fontColor, setFontColor] = useState(props.fontColor ?? '');
  const [quickQuestionPillBgColor, setQuickQuestionPillBgColor] = useState(props.quickQuestionPillBgColor ?? '');
  const [welcomeMessage, setWelcomeMessage] = useState(props.welcomeMessage ?? 'Hi! What can I help you with?');
  const [botStatusText, setBotStatusText] = useState(props.botStatusText ?? '');
  const [liveChatEnabled, setLiveChatEnabled] = useState(props.liveChatEnabled ?? true);
  const [liveChatMessage, setLiveChatMessage] = useState(props.liveChatMessage ?? '');
  const [quickQuestionsVisibility, setQuickQuestionsVisibility] = useState(props.quickQuestionsVisibility ?? {});

  // Sync state with props in admin mode for live preview
  useEffect(() => {
    if (props.mode === 'admin') {
      setHeader(props.header ?? '');
      setHeaderBgColor(props.headerBgColor ?? '');
      setFooterBgColor(props.footerBgColor ?? '');
      setFont(props.font ?? '');
      setFontSize(props.fontSize ?? '');
      setFontColor(props.fontColor ?? '');
      setChatBgColor(props.chatBgColor ?? '');
      setChatInputBgColor(props.chatInputBgColor ?? '');
      setLogoUrl(props.logoUrl ?? '');
      setBotName(props.botName ?? '');
      setBotStatusText(props.botStatusText ?? '');
      setLiveChatMessage(props.liveChatMessage ?? '');
      setShape(props.shape ?? '');
      setPosition(props.position ?? '');
      setLiveChatEnabled(props.liveChatEnabled ?? true);
      setQuickQuestionPillBgColor(props.quickQuestionPillBgColor ?? '');
      setWelcomeMessage(props.welcomeMessage ?? 'Hi! What can I help you with?');
      setQuickQuestionsVisibility(props.quickQuestionsVisibility ?? {});
      setChatBubbleOpenColor(props.chatBubbleOpenColor || '#2D2D2D');
      setChatBubbleClosedColor(props.chatBubbleClosedColor || '#2D2D2D');
      setBotBubbleColor(props.botBubbleColor || '#333333');
      setUserBubbleColor(props.userBubbleColor || '#0B93F6');
    }
  }, [
    props.mode,
    props.header,
    props.headerBgColor,
    props.footerBgColor,
    props.font,
    props.fontSize,
    props.fontColor,
    props.chatBgColor,
    props.chatInputBgColor,
    props.logoUrl,
    props.botName,
    props.botStatusText,
    props.liveChatMessage,
    props.shape,
    props.position,
    props.liveChatEnabled,
    props.quickQuestionPillBgColor,
    props.welcomeMessage,
    props.quickQuestionsVisibility,
    props.chatBubbleOpenColor,
    props.chatBubbleClosedColor,
    props.botBubbleColor,
    props.userBubbleColor
  ]);

  // Fetch customization settings from backend for production (not admin preview)
  useEffect(() => {
    if (props.mode === 'admin') return; // Only fetch in production
    async function fetchCustomization() {
      const shopDomain = getShopDomain();

      if (!shopDomain) {
        return;
      }
      try {
        const url = `${API_BASE}/customization?shop=${shopDomain}`;

        const res = await fetch(url);

        if (!res.ok) {
          return;
        }
        const data = await res.json();

        if (typeof data.chatboxEnabled === 'boolean') { setChatboxEnabled(data.chatboxEnabled); }
        if (typeof data.logoUrl === 'string') { setLogoUrl(data.logoUrl); }
        if (typeof data.header === 'string') { setHeader(data.header); }
        if (typeof data.font === 'string') { setFont(data.font); }
        if (typeof data.fontSize === 'string') { setFontSize(data.fontSize); }
        if (typeof data.botName === 'string') { setBotName(data.botName); }
        if (typeof data.position === 'string') { setPosition(data.position); }
        if (typeof data.shape === 'string') { setShape(data.shape); }
        if (typeof data.headerBgColor === 'string') { setHeaderBgColor(data.headerBgColor); }
        if (typeof data.chatBgColor === 'string') { setChatBgColor(data.chatBgColor); }
        if (typeof data.footerBgColor === 'string') { setFooterBgColor(data.footerBgColor); }
        if (typeof data.chatInputBgColor === 'string') { setChatInputBgColor(data.chatInputBgColor); }
        if (typeof data.fontColor === 'string') { setFontColor(data.fontColor); }
        if (typeof data.quickQuestionPillBgColor === 'string') { setQuickQuestionPillBgColor(data.quickQuestionPillBgColor); }
        if (typeof data.chatBubbleOpenColor === 'string') { setChatBubbleOpenColor(data.chatBubbleOpenColor); }
        if (typeof data.chatBubbleClosedColor === 'string') { setChatBubbleClosedColor(data.chatBubbleClosedColor); }
        if (typeof data.botBubbleColor === 'string') { setBotBubbleColor(data.botBubbleColor); }
        if (typeof data.userBubbleColor === 'string') { setUserBubbleColor(data.userBubbleColor); }
        if (typeof data.welcomeMessage === 'string') { setWelcomeMessage(data.welcomeMessage); }
        if (typeof data.botStatusText === 'string') { setBotStatusText(data.botStatusText); }
        if (typeof data.liveChatEnabled === 'boolean') { setLiveChatEnabled(data.liveChatEnabled); }
        if (typeof data.liveChatMessage === 'string') { setLiveChatMessage(data.liveChatMessage); }
        // Parse quickQuestionsVisibility if it's a string
        let qv = data.quickQuestionsVisibility;
        if (typeof qv === 'string') {
          try {
            qv = JSON.parse(qv);
          } catch (e) {
            console.error('[Chatbot] Failed to parse quickQuestionsVisibility:', qv, e);
            qv = null;
          }
        }
        if (qv && typeof qv === 'object') {
          setQuickQuestionsVisibility(qv);
        }
      } catch (err) {
        // Optionally, show error toast or log
        console.error('[Chatbot] Error fetching customization', err);
      }
    }
    fetchCustomization();
  }, [props.mode]);

  const [isOpen, setIsOpen] = useState(props.open ?? false);
  console.log("[ChatBot] props", props);
  // If props.open is provided, always use it in preview (admin customization)
  useEffect(() => {
    if (typeof props.open === 'boolean') {
      setIsOpen(props.open);
    }
  }, [props.open]);
  const [messages, setMessages] = useState([
    { role: "assistant", content: welcomeMessage || "Hi! What can I help you with?" }
  ]);


  // Keep welcome message in sync in admin/preview mode
  useEffect(() => {
    if (!hasInteracted && messages.length === 1 && messages[0].role === 'assistant') {
      setMessages([{ role: 'assistant', content: welcomeMessage || "Hi! What can I help you with?" }]);
    }
  }, [welcomeMessage]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [recommendProductId, setRecommendProductId] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLiveChat, setIsLiveChat] = useState(false);
  const [liveChatConversationId, setLiveChatConversationId] = useState(() => {
    return localStorage.getItem('liveChatConversationId') || '';
  });
  const [liveChatStatus, setLiveChatStatus] = useState('pending');
  const [plan, setPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [isLiveChatAvailable, setIsLiveChatAvailable] = useState(true);
  const messagesEndRef = useRef(null);

  // Always scroll to bottom when messages change or chat is opened
  useEffect(() => {
    // Use a microtask to ensure all DOM updates are flushed
    Promise.resolve().then(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
      }
    });
  }, [messages, isOpen]);

  // Initialize access token
  useEffect(() => {
    const el = document.getElementById('shopify-chatbot-container');
    if (el && el.dataset.accessToken) {
      setAccessToken(el.dataset.accessToken);
    }
    
    if (!messages.length) return;
    const lastMsg = messages[messages.length - 1];
    if (
      lastMsg.role === "assistant" &&
      typeof lastMsg.content === "string" &&
      lastMsg.content.includes("data-product-id")
    ) {
      const match = lastMsg.content.match(/data-product-id=['"](\d+)['"]/);
      if (match) setRecommendProductId(match[1]);
    }
  }, [messages]);

  // Fetch current plan on mount
  useEffect(() => {
    async function fetchPlan() {
      const shopDomain = getShopDomain();
      if (!shopDomain) return;
      try {
        const res = await fetch(`${API_BASE}/current-plan?shop=${shopDomain}`);
        const data = await res.json();
        setPlan(data.plan);
        setFeatures(getResolvedFeatures(data.plan, plans));
      } catch (err) {
        setPlan('Free');
        setFeatures(getResolvedFeatures('Free', plans));
      }
    }
    fetchPlan();
  }, []);

  useEffect(() => {
    const shopDomain = getShopDomain();
    if (!shopDomain) return;
    checkStoreOpen(shopDomain).then(val => {
      setIsLiveChatAvailable(val);
    });
  }, []);

  // Unified quick questions definition (must match admin keys)
  const allQuickQuestions = [
    { key: 'product_recommendations', label: '✨ Show me product recommendations', feature: 'General product recommendations' },
    { key: 'order_tracking', label: '📦 How do I track my order?', feature: 'Order tracking' },
    { key: 'shipping_times', label: '🚚 What are your shipping times?', feature: null },
    { key: 'contact_support', label: '💬 How can I contact support?', feature: null },
    { key: 'return_policy', label: '🔄 What is your return policy?', feature: null },
  ];

  console.log('[Chatbot] quickQuestionsVisibility:', quickQuestionsVisibility);
  console.log('[Chatbot] features:', features);
  console.log('[Chatbot] props.quickQuestions:', props.quickQuestions);
  let quickQuestions = allQuickQuestions; // Default: show all available in the plan
  if (props.quickQuestions) {
    quickQuestions = props.quickQuestions;
  } else if (Array.isArray(quickQuestionsVisibility) && quickQuestionsVisibility.length > 0) {
    quickQuestions = allQuickQuestions.filter(q => quickQuestionsVisibility.includes(q.key));
  } else if (
    typeof quickQuestionsVisibility === 'object' &&
    quickQuestionsVisibility !== null &&
    Object.keys(quickQuestionsVisibility).length > 0
  ) {
    quickQuestions = allQuickQuestions.filter(q => quickQuestionsVisibility[q.key]);
  }
  console.log('[Chatbot] Final quickQuestions:', quickQuestions);

  const canShowGeneralRecommendations = features.includes('General product recommendations');
  const canShowOrderRecommendations = features.includes('Order based product recommendations');

  // Utility to get the current shop domain dynamically
  function getShopDomain() {
    // Admin preview: allow shopDomain prop override
    if (props.shopDomain) return props.shopDomain;
    // Try Shopify global variable (if embedded in Shopify store)
    if (window.Shopify && window.Shopify.shop) return window.Shopify.shop;
    // Try extracting from window.location.hostname
    if (window.location.hostname.endsWith('.myshopify.com')) return window.location.hostname;
    // Fallback: use a data attribute or prompt for configuration
    const el = document.getElementById('shopify-chatbot-container');
    if (el && el.dataset.shop) return el.dataset.shop;
    // In development or test, provide a mock shop domain for robust testing
    if (process.env.NODE_ENV !== 'production') {
      return 'test-shop.myshopify.com';
    }
    return null;
  }

  // Utility to get the current shop currency dynamically
  function getShopCurrency() {
    // Try Shopify global variable (if embedded in Shopify store)
    if (window.Shopify && window.Shopify.currency) return window.Shopify.currency;
    // Try extracting from a data attribute
    const el = document.getElementById('shopify-chatbot-container');
    if (el && el.dataset.currency) return el.dataset.currency;
    // Fallback: use USD or prompt for configuration
    return 'USD';
  }

  // Helper to strip emojis from a string
  function stripEmojis(str) {
    return str.replace(/[\u{1F600}-\u{1F6FF}\u{1F300}-\u{1F5FF}\u{1F900}-\u{1F9FF}\u{1F1E6}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2B50}\u{23CF}\u{23E9}-\u{23FA}\u{24C2}]/gu, '').trim();
  }

  // Send a message to live chat
  const handleSendLiveChatMessage = async (messageToSend) => {
    if (!liveChatConversationId) {
      setMessages((msgs) => [...msgs, { role: 'assistant', content: 'Live chat session not started.' }]);
      return;
    }
    const shopDomain = getShopDomain();
    if (!shopDomain) {
      setMessages((msgs) => [...msgs, { role: 'assistant', content: 'Missing shop domain. Cannot send live chat message.' }]);
      return;
    }
    setLoading(true);
    try {
      // Immediately show user's message
      setMessages((msgs) => [...msgs, { role: 'user', content: messageToSend }]);
      setInput("");
      await fetch(`${API_BASE}/livechat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: shopDomain,
          conversationId: liveChatConversationId,
          sender: 'user',
          text: messageToSend,
        }),
      });
    } catch (err) {
      setMessages((msgs) => [...msgs, { role: 'assistant', content: 'Error sending live chat message.' }]);
    } finally {
      setLoading(false);
    }
  };

  // Unified send handler: accepts optional message (for pills/quick replies)
  const handleSend = async (customMessage) => {
    // Prevent sending if in admin mode (preview)
    if (props.mode === 'admin') {
      return;
    }
    const messageToSend = typeof customMessage === 'string' ? customMessage : input;
    if (!messageToSend.trim()) return;
    setHasInteracted(true);
    setLoading(true);
    if (isLiveChat && liveChatConversationId) {
      setLoading(false); // We'll handle loading in handleSendLiveChatMessage
      handleSendLiveChatMessage(messageToSend);
      return;
    }
    const shopDomain = getShopDomain();
    const currency = getShopCurrency();
    if (!shopDomain) {
      setMessages((msgs) => [...msgs, { role: "assistant", content: "Sorry, missing shop domain. Cannot send message." }]);
      setLoading(false);
      return;
    }
    const newMessages = [...messages, { role: "user", content: messageToSend }];
    setMessages(newMessages);
    setInput("");
    try {
      const response = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: newMessages, shop: shopDomain, currency }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setMessages((msgs) => [...msgs, { role: "assistant", content: data.reply }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages((msgs) => [...msgs, { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again later." }]);
    } finally {
      setLoading(false);
    }
  };

  // Ensure pill/quick reply click sends message immediately
  const handlePillClick = (pillText) => {
    const cleanText = stripEmojis(pillText);
    if (
      cleanText === "Show Recommendations"
    ) {
      handleShowRecommendations();
    } else {
      handleSend(cleanText);
    }
  };

  const handleShowRecommendations = async () => {
    if (!canShowGeneralRecommendations) return;
    if (!recommendProductId) return;
    setLoading(true);
    try {
      const shopDomain = getShopDomain();
      if (!shopDomain) {
        setMessages((msgs) => [...msgs, { role: "assistant", content: "Sorry, missing shop domain. Cannot fetch recommendations." }]);
        setLoading(false);
        return;
      }
      const currency = getShopCurrency();
      const response = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: messages, shop: shopDomain, recommendProductId, currency }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setMessages((msgs) => [...msgs, { role: "assistant", content: data.reply }]);
      setRecommendProductId(null); // Hide button after use
    } catch (err) {
      setMessages((msgs) => [...msgs, { role: "assistant", content: "Sorry, there was an error fetching recommendations." }]);
    } finally {
      setLoading(false);
    }
  };

  // When starting live chat, preserve previous messages and add a system divider
  const handleStartLiveChat = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/livechat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: getShopDomain(),
          customerId: getUserId() // or however you identify the user
        })
      });
      const data = await response.json();
      if (data.conversationId) {
        setLiveChatConversationId(data.conversationId);
        setIsLiveChat(true);
        setLiveChatStatus('pending');
        setMessages(msgs => [
          ...msgs,
          { role: 'system', content: '──────────── Waiting for an agent ────────────' }
        ]);
      } else {
        setMessages(msgs => [...msgs, { role: 'assistant', content: 'Error starting live chat.' }]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to show "Live Chat Started" divider when agent joins
  const maybeInsertLiveChatStartedDivider = (msgs, status) => {
    // status should be 'pending' or 'active'
    const hasWaiting = msgs.some(m => m.role === 'system' && m.content.toLowerCase().includes('waiting for an agent'));
    const hasStarted = msgs.some(m => m.role === 'system' && m.content.toLowerCase().includes('live chat started'));
    if (status === 'active' && hasWaiting && !hasStarted) {
      // Replace "Waiting for an agent" with "Live chat started"
      return msgs.map(m =>
        m.role === 'system' && m.content.includes('Waiting for an agent')
          ? { ...m, content: '──────────── Live chat started ────────────' }
          : m
      );
    }
    return msgs;
  };

  // Handler to exit live chat
  const handleExitLiveChat = async (endedByAgent = false) => {
    setLoading(true);
    try {
      if (!endedByAgent) {
        await fetch(`${API_BASE}/livechat/close`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shop: getShopDomain(),
            conversationId: liveChatConversationId,
            endedBy: 'user'
          })
        });
      }
      setMessages(msgs => [
        ...msgs,
        { role: 'system', content: endedByAgent ? 'Live chat ended by the agent.' : 'Chat ended. Thank you for chatting with us!' }
      ]);
      setIsLiveChat(false);
      setLiveChatConversationId(null);
    } catch (err) {
      setMessages(msgs => [...msgs, { role: 'assistant', content: 'Error ending live chat.' }]);
    }
    setLoading(false);
  };

  // Polling for live chat messages
  useEffect(() => {
    let pollingInterval;
    if (isLiveChat && liveChatConversationId) {
      const shopDomain = getShopDomain();
      const fetchHistory = async () => {
        if (!shopDomain || !liveChatConversationId) return;
        try {
          const res = await fetch(`${API_BASE}/livechat/history?shop=${encodeURIComponent(shopDomain)}&conversationId=${encodeURIComponent(liveChatConversationId)}&role=user`);
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.messages)) {
              setMessages((prevMsgs) => {
                // Remove all previous user/assistant messages after divider
                const dividerIdx = prevMsgs.findIndex(m => m.role === 'system' && (m.content.includes('Live chat started') || m.content.includes('Waiting for an agent') || m.content.includes('Live Chat Ended') || m.content.includes('Chat ended by agent.')));
                const baseMsgs = dividerIdx === -1 ? prevMsgs : prevMsgs.slice(0, dividerIdx + 1);
                // Filter out extra system ended messages from backend
                const liveChatMsgs = data.messages
                  .filter(msg => !(msg.sender === 'system' && msg.text && (msg.text.toLowerCase().includes('ended by') || msg.text.includes('Live Chat Ended'))))
                  .map(msg => ({
                    role: msg.sender === 'user' ? 'user' : (msg.sender === 'system' ? 'system' : 'assistant'),
                    content: msg.text
                  }));

                // --- Fallback: if any agent/assistant message arrives and divider is still 'Waiting for an agent', update it ---
                const hasWaiting = baseMsgs.some(m => m.role === 'system' && m.content.includes('Waiting for an agent'));
                const hasStarted = baseMsgs.some(m => m.role === 'system' && m.content.includes('Live chat started'));
                const agentMsgExists = liveChatMsgs.some(m => m.role === 'assistant');
                if (hasWaiting && !hasStarted && agentMsgExists) {
                  return baseMsgs.map(m =>
                    m.role === 'system' && m.content.includes('Waiting for an agent')
                      ? { ...m, content: '──────────── Live chat started ────────────' }
                      : m
                  ).concat(liveChatMsgs);
                }

                // --- Status-based logic for Ended ---
                const endedSystemMsg = data.status === 'closed'
                  ? data.messages.find(
                      msg => msg.sender === 'system' && msg.text && msg.text.toLowerCase().includes('ended by')
                    )
                  : undefined;

                if (data.status === 'closed' && endedSystemMsg) {
                  // Do NOT replace any existing divider, just append a new divider at the end
                  const updatedMsgs = [...baseMsgs, ...liveChatMsgs];
                  // Only append if not already present at the end
                  if (
                    updatedMsgs.length === 0 ||
                    updatedMsgs[updatedMsgs.length - 1].content !== endedSystemMsg.text
                  ) {
                    updatedMsgs.push({
                      role: 'system',
                      content: endedSystemMsg.text
                    });
                  }
                  setIsLiveChat(false);
                  return updatedMsgs;
                }

                // Always run through maybeInsertLiveChatStartedDivider as a last check
                return maybeInsertLiveChatStartedDivider([...baseMsgs, ...liveChatMsgs], data.status);
              });
              setLiveChatStatus(data.status);
              if (data.status === 'closed' && data.endedBy === 'agent') {
                handleExitLiveChat(true);
              }
            }
          }
        } catch (err) {
          // Optionally handle error
        }
      };
      pollingInterval = setInterval(fetchHistory, 2000); // 2 seconds
      fetchHistory();
    }
    return () => { if (pollingInterval) clearInterval(pollingInterval); };
  }, [isLiveChat, liveChatConversationId]);

  // Google Analytics for Chat Bubble (widget tag)
  useEffect(() => {
    ensureGoogleAnalytics('G-EZHBH5S15W');
  }, []);

  function trackChatBubbleOpen() {
    trackEvent('chat_bubble_open', {
      event_category: 'engagement',
      event_label: 'Chatbot Widget',
    });
  }

  useEffect(() => {
    if (isOpen) {
      trackChatBubbleOpen();
    }
  }, [isOpen]);

  // Helper to parse UTC offset string (e.g. 'UTC+05:30', 'UTC−10:00', 'UTC±00:00')
  function parseUtcOffset(offsetStr) {
    // Normalize minus sign
    offsetStr = offsetStr.replace('−', '-').replace('±', '+').replace('UTC', '').replace(' ', '');
    if (offsetStr === '+00:00' || offsetStr === '00:00' || offsetStr === '') return 0;
    const match = offsetStr.match(/([+-])(\d{2}):(\d{2})/);
    if (!match) return 0;
    const sign = match[1] === '-' ? -1 : 1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    return sign * (hours * 60 + minutes);
  }

  // Helper to determine if store is open
  async function checkStoreOpen(shopDomain) {
    try {
      const res = await fetch(`${API_BASE}/store-hours?shop=${shopDomain}`);
      const data = await res.json();
      const nowUtc = new Date();
      const hours = Array.isArray(data?.hours) ? data.hours : [];
      const timeZone = data?.timeZone || 'UTC±00:00';
      const restrictLiveChat = data?.restrictLiveChat;
      if (!restrictLiveChat) return true; // Show at all times if not restricted
      if (!hours.length) return true; // If no hours set, assume open
      // Calculate store local time from UTC offset
      const offsetMins = parseUtcOffset(timeZone);
      const utcMs = nowUtc.getTime() + (nowUtc.getTimezoneOffset() * 60000); // pure UTC ms
      const local = new Date(utcMs + offsetMins * 60000);
      const localMins = local.getHours() * 60 + local.getMinutes();
      let localDay = local.getDay(); // 0 = Sunday, 1 = Monday, ...
      // Map JS Sunday=0 to our Monday=0
      const dayName = daysOfWeek[localDay === 0 ? 6 : localDay - 1];
      const today = hours.find(h => h.day === dayName);
      if (!today || !today.enabled) return false;
      if (today.allDay) return true;
      // Check if current time is within open/close
      const [openHour, openMin] = today.open.split(':').map(Number);
      const [closeHour, closeMin] = today.close.split(':').map(Number);
      if (isNaN(openHour) || isNaN(closeHour)) return false;
      const openMins = openHour * 60 + (openMin || 0);
      const closeMins = closeHour * 60 + (closeMin || 0);
      return localMins >= openMins && localMins <= closeMins;
    } catch (err) {
      return true; // On error, assume open
    }
  }

  // Example usage: hide/show chat button based on store hours
  // useEffect(() => {
  //   const shopDomain = getShopDomain();
  //   if (!shopDomain) return;
  //   checkStoreOpen(shopDomain).then(isOpen => {
  //     setIsOpen(isOpen);
  //   });
  // }, []);

  const renderMessage = (content) => {
    if (typeof content !== 'string') {
      return content === undefined || content === null ? '' : String(content);
    }
    // Remove leading whitespace/newlines
    let trimmed = content.replace(/^[\s\n]+/, '');
    // Remove leading <br>, <p></p>, or empty tags
    trimmed = trimmed.replace(/^(<br\s*\/?>|<p>\s*<\/p>)+/i, '');
    if (/<[a-z][\s\S]*>/i.test(trimmed)) {
      return <div dangerouslySetInnerHTML={{ __html: trimmed }} />;
    }
    return trimmed;
  };

  return (
    <div
      id="shopify-chatbot-container"
      style={{
        position: 'fixed',
        bottom: 20,
        zIndex: 10000,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        ...(position && position.toLowerCase() === 'bottom-left'
            ? { left: 20, right: 'auto' }
            : { right: 20, left: 'auto' }
        )
      }}
    >

      <div style={{
        position: 'absolute',
        bottom: '80px',
        ...(position && position.toLowerCase() === 'bottom-left'
          ? { left: 0, right: 'auto' }
          : { right: 0, left: 'auto' }
        ),
        width: '380px',
        height: '666px',
        backgroundColor: 'black',
        borderRadius: '20px',
        boxShadow: '0 0 20px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? 'visible' : 'hidden',
        transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.3s ease-in-out'
      }}>
        <div style={{
          padding: '14px 20px 12px 20px',
          borderBottom: '1px solid #333',
          display: 'flex',
          flexDirection: 'column',
          background: headerBgColor || 'linear-gradient(90deg, #232526 0%, #414345 100%)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          <h1 style={{
            fontSize: '21px',
            fontWeight: '600',
            color: fontColor || 'white',
            margin: 0,
            fontFamily: 'inherit',
            letterSpacing: '-0.5px',
            textShadow: '0 1px 2px rgba(0,0,0,0.10)'
          }}>{header || 'Welcome!'}</h1>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: 6,
            minHeight: 20,
          }}>
            {isLiveChat ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#22c55e',
                  marginRight: 8,
                  boxShadow: '0 0 4px #22c55e99',
                }}></span>
                <span style={{ color: '#bbb', fontWeight: 300, fontSize: 12 }}>You are talking to a live agent</span>
              </>
            ) : (
              <span style={{ color: '#bbb', fontWeight: 300, fontSize: 12 }}>{botStatusText || 'You are talking to a bot'}</span>
            )}
          </div>
        </div>

        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          background: chatBgColor
        }}>
          {messages.map((msg, index) => {
            // Live chat started divider
            if (msg.role === 'system' && msg.content.toLowerCase().includes('live chat started')) {
              return (
                <p key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: 12,
                  color: '#666',
                  margin: '24px 0',
                  lineHeight: '17.58px',
                  width: '100%',
                  position: 'relative',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  whiteSpace: 'nowrap',
                  justifyContent: 'center',
                }}>
                  <span style={{
                    display: 'inline-block',
                    height: 1,
                    width: '40%',
                    backgroundColor: '#d9d9d9',
                    opacity: 0.65,
                    verticalAlign: 'middle',
                    marginRight: 8,
                  }}></span>
                  <span style={{whiteSpace: 'nowrap'}}>Live Chat Started</span>
                  <span style={{
                    display: 'inline-block',
                    height: 1,
                    width: '40%',
                    backgroundColor: '#d9d9d9',
                    opacity: 0.65,
                    verticalAlign: 'middle',
                    marginLeft: 8,
                  }}></span>
                </p>
              );
            }
            // Live chat ended divider
            if (msg.role === 'system' && msg.content.toLowerCase().includes('chat ended')) {
              return (
                <p key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: 12,
                  color: '#666',
                  margin: '24px 0',
                  lineHeight: '17.58px',
                  width: '100%',
                  position: 'relative',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  whiteSpace: 'nowrap',
                  justifyContent: 'center',
                }}>
                  <span style={{
                    display: 'inline-block',
                    height: 1,
                    width: '40%',
                    backgroundColor: '#d9d9d9',
                    opacity: 0.65,
                    verticalAlign: 'middle',
                    marginRight: 8,
                  }}></span>
                  <span style={{whiteSpace: 'nowrap'}}>Live Chat Ended</span>
                  <span style={{
                    display: 'inline-block',
                    height: 1,
                    width: '40%',
                    backgroundColor: '#d9d9d9',
                    opacity: 0.65,
                    verticalAlign: 'middle',
                    marginLeft: 8,
                  }}></span>
                </p>
              );
            }
            // Waiting for an agent divider
            if (msg.role === 'system' && msg.content.toLowerCase().includes('waiting for an agent')) {
              return (
                <p key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: 12,
                  color: '#666',
                  margin: '24px 0',
                  lineHeight: '17.58px',
                  width: '100%',
                  position: 'relative',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  whiteSpace: 'nowrap',
                  justifyContent: 'center',
                }}>
                  <span style={{
                    display: 'inline-block',
                    height: 1,
                    width: '40%',
                    backgroundColor: '#d9d9d9',
                    opacity: 0.65,
                    verticalAlign: 'middle',
                    marginRight: 8,
                  }}></span>
                  <span style={{whiteSpace: 'nowrap'}}>Waiting for an agent</span>
                  <span style={{
                    display: 'inline-block',
                    height: 1,
                    width: '40%',
                    backgroundColor: '#d9d9d9',
                    opacity: 0.65,
                    verticalAlign: 'middle',
                    marginLeft: 8,
                  }}></span>
                </p>
              );
            }
            // Default rendering for other messages
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  marginBottom: 18,
                  flexDirection: msg.role === "assistant" ? 'row' : 'row-reverse'
                }}
              >
                {msg.role === 'assistant' && (
                  <div style={{ position: 'relative', width: 36, minWidth: 36, height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', marginRight: 5 }}>
                    {/* Use the closed chat bubble SVG as avatar */}
                    <svg width="36" height="36" viewBox="0 0 842 768" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', background: '#222', borderRadius: '50%', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}>
                      <path d="M421 40C441.882 40 459 57.1177 459 78V120.156C613.746 127.542 758 202.194 758 384C758 521.658 645.658 634 508 634H484.5L421 695.5L357.5 634H334C196.342 634 84 521.658 84 384C84 202.194 228.254 127.542 383 120.156V78C383 57.1177 400.118 40 421 40ZM421 192C269.835 192 148 253.026 148 384C148 483.45 236.55 572 336 572H372.5L421 620.5L469.5 572H506C605.45 572 694 483.45 694 384C694 253.026 572.165 192 421 192ZM347 384C373.614 384 395 405.386 395 432C395 458.614 373.614 480 347 480C320.386 480 299 458.614 299 432C299 405.386 320.386 384 347 384ZM495 384C521.614 384 543 405.386 543 432C543 458.614 521.614 480 495 480C468.386 480 447 458.614 447 432C447 405.386 468.386 384 495 384Z" fill="white"/>
                    </svg>
                  </div>
                )}
                <div style={{
                  position: 'relative',
                  alignSelf: msg.role === "assistant" ? "flex-end" : "flex-end",
                  backgroundColor: msg.role === "assistant" ? botBubbleColor || '#333333' : userBubbleColor || '#0B93F6',
                  color: fontColor || 'white',
                  padding: '10px 16px',
                  borderRadius: '20px',
                  maxWidth: '80%',
                  opacity: 1,
                  transform: 'translateY(0)',
                  transition: 'all 0.3s ease-in-out',
                  animationName: 'slideIn',
                  animationDuration: '0.3s',
                  animationFillMode: 'forwards',
                  fontSize: '15px',
                  lineHeight: '1.4',
                  letterSpacing: '-0.2px'
                }}>
                  {renderMessage(msg.content)}
                </div>
              </div>
            );
          })}
          {loading && (
            <div style={{
              alignSelf: "flex-start",
              backgroundColor: chatBgColor || '#333333',
              color: fontColor || 'white',
              padding: '10px 16px',
              borderRadius: '20px',
              display: 'flex',
              gap: '4px'
            }}>
              <span style={{ width: '6px', height: '6px', backgroundColor: 'white', borderRadius: '50%', animation: 'bounce 1s infinite' }}></span>
              <span style={{ width: '6px', height: '6px', backgroundColor: 'white', borderRadius: '50%', animation: 'bounce 1s infinite', animationDelay: '0.2s' }}></span>
              <span style={{ width: '6px', height: '6px', backgroundColor: 'white', borderRadius: '50%', animation: 'bounce 1s infinite', animationDelay: '0.4s' }}></span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div style={{
          padding: '20px',
          borderTop: '1px solid #333',
          background: footerBgColor
        }}>
          {/* Quick Questions Buttons */}
          {isOpen && !hasInteracted && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '12px',
              justifyContent: 'center',
            }}>
              {quickQuestions.map((q) => (
                <button
                  key={q.key}
                  onClick={() => handlePillClick(q.label)}
                  style={{
                    background: quickQuestionPillBgColor || '#222',
                    color: fontColor || 'white',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '6px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'background 0.2s',
                  }}
                  onMouseDown={e => e.preventDefault()}
                >
                  {q.label}
                </button>
              ))}
            </div>
          )}
          {/* Recommendations Quick Reply Button (if present) */}
          {isOpen && canShowGeneralRecommendations && recommendProductId && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
              <button
                onClick={handleShowRecommendations}
                style={{
                  border: 'none',
                  background: '#333',
                  color: '#fff',
                  padding: '12px 28px',
                  borderRadius: '999px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  margin: '0 auto',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                  transition: 'background 0.2s',
                  outline: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onMouseOver={e => e.currentTarget.style.background = "#222"}
                onMouseOut={e => e.currentTarget.style.background = "#333"}
              >
                Show Recommendations
              </button>
            </div>
          )}
          {/* Live Chat Control Button */}
          {(liveChatEnabled === undefined || liveChatEnabled) && features.includes('Live chat') && isLiveChatAvailable && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              {isLiveChat ? (
                <button
                  onClick={props.mode === 'admin' ? undefined : () => handleExitLiveChat(false)}
                  disabled={loading || props.mode === 'admin'}
                  style={{
                    padding: '7px 16px', 
                    borderRadius: 20,
                    background: 'linear-gradient(90deg,#ff758c 0%,#ff7eb3 100%)',
                    color: fontColor || 'white',
                    border: 'none',
                    fontWeight: 500,
                    fontSize: 14,
                    boxShadow: '0 1.5px 5px rgba(255,117,140,0.10)',
                    transition: 'background 0.18s, box-shadow 0.18s',
                    letterSpacing: '-0.2px',
                    cursor: (loading || props.mode === 'admin') ? 'not-allowed' : 'pointer',
                    opacity: (loading || props.mode === 'admin') ? 0.7 : 1,
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    minHeight: 0,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    textTransform: 'none',
                    fontFamily: 'inherit',
                    lineHeight: 1.1,
                  }}
                  tabIndex={0}
                  aria-label="Exit Live Chat"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: 5}}><path d="M6 6l8 8M6 14L14 6" stroke="#fff" strokeWidth="2.1" strokeLinecap="round"/></svg>
                  Exit Live Chat
                </button>
              ) : (
                <button
                  onClick={props.mode === 'admin' ? undefined : handleStartLiveChat}
                  disabled={loading || props.mode === 'admin'}
                  style={{
                    padding: '7px 16px', 
                    borderRadius: 20,
                    background: 'linear-gradient(90deg,#43cea2 0%,#185a9d 100%)',
                    color: fontColor || 'white',
                    border: 'none',
                    fontWeight: 500,
                    fontSize: 14,
                    boxShadow: '0 1.5px 5px rgba(67,206,162,0.10)',
                    transition: 'background 0.18s, box-shadow 0.18s',
                    letterSpacing: '-0.2px',
                    cursor: (loading || props.mode === 'admin') ? 'not-allowed' : 'pointer',
                    opacity: (loading || props.mode === 'admin') ? 0.7 : 1,
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    minHeight: 0,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    textTransform: 'none',
                    fontFamily: 'inherit',
                    lineHeight: 1.1,
                  }}
                  tabIndex={0}
                  aria-label="Talk to an Agent"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: 5}}><path d="M10 3v14M3 10h14" stroke="#fff" strokeWidth="2.1" strokeLinecap="round"/></svg>
                  <span>{liveChatMessage ? liveChatMessage : 'Talk to an Agent'}</span>
                </button>
              )}
            </div>
          )}
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <input
              type="text"
              value={input}
              onChange={(e) => { setInput(e.target.value); setHasInteracted(true); }}
              onKeyPress={(e) => {
                if (props.mode !== 'admin' && e.key === 'Enter') handleSend();
                if (props.mode === 'admin') e.preventDefault();
              }}
              placeholder={props.mode === 'admin' ? 'Message sending disabled in preview' : 'Type your message...'}
              style={{
                width: '100%',
                padding: '12px 16px',
                paddingRight: '48px',
                backgroundColor: chatInputBgColor || '#333333',
                border: 'none',
                borderRadius: '24px',
                color: fontColor || 'white',
                outline: 'none',
                fontSize: '15px',
                fontFamily: 'inherit',
                opacity: props.mode === 'admin' ? 0.6 : 1,
                cursor: props.mode === 'admin' ? 'not-allowed' : 'text',
              }}
              disabled={props.mode === 'admin'}
            />
            <button
              onClick={() => handleSend()}
              style={{
                position: 'absolute',
                right: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: props.mode === 'admin' ? 'not-allowed' : 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: props.mode === 'admin' ? 0.5 : 1,
              }}
              disabled={props.mode === 'admin'}
              title={props.mode === 'admin' ? 'Message sending is disabled in preview' : undefined}
              aria-label="send"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="white" aria-hidden="true">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
              </svg>
              <span style={{ display: 'none' }}>Send</span>
            </button>
          </div>
          {/* Footer: Powered by chatbot */}
          {(plan === 'Free' || plan === 'Basic') && (
            <div style={{
              textAlign: 'center',
              marginTop: 8,
              fontSize: 13,
              color: '#b4b8c7',
              paddingBottom: 12
            }}>
              Powered by <a href="https://www.google.com" target="_blank" rel="noopener noreferrer" style={{ color: '#4bb3fd', textDecoration: 'underline', fontWeight: 600 }}>{config.APP_HANDLE}</a>
            </div>
          )}
        </div>

      </div>

      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '60px',
          height: '60px',
          backgroundColor: isOpen
            ? (chatBubbleOpenColor || '#2D2D2D')
            : (chatBubbleClosedColor || '#2D2D2D'),
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: isOpen ? 'rotate(360deg)' : 'rotate(0)',
          transition: 'transform 0.3s ease-in-out'
        }}
      >
        {isOpen ? (
          // X icon when open
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="16" fill="black" />
            <line x1="10" y1="10" x2="22" y2="22" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="22" y1="10" x2="10" y2="22" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          // Improved chat bubble logo (no black rect, centered, fits button)
          <svg width="46" height="46" viewBox="0 0 842 768" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
            <path d="M421 40C441.882 40 459 57.1177 459 78V120.156C613.746 127.542 758 202.194 758 384C758 521.658 645.658 634 508 634H484.5L421 695.5L357.5 634H334C196.342 634 84 521.658 84 384C84 202.194 228.254 127.542 383 120.156V78C383 57.1177 400.118 40 421 40ZM421 192C269.835 192 148 253.026 148 384C148 483.45 236.55 572 336 572H372.5L421 620.5L469.5 572H506C605.45 572 694 483.45 694 384C694 253.026 572.165 192 421 192ZM347 384C373.614 384 395 405.386 395 432C395 458.614 373.614 480 347 480C320.386 480 299 458.614 299 432C299 405.386 320.386 384 347 384ZM495 384C521.614 384 543 405.386 543 432C543 458.614 521.614 480 495 480C468.386 480 447 458.614 447 432C447 405.386 468.386 384 495 384Z" fill="white"/>
          </svg>
        )}
      </button>

      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
        `}
      </style>
    </div>
  );
}